import { compact, uniq } from "lodash";

import { ModelEventConstructor } from "../../utils/ModelEventConstructor";
import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import { createObjectLanguageNames } from "../../assets/languages/langs";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import { SortingOrder } from "../../types/SortingOrder";
import SubscriptionBase from "../../types/Subscription";
import { Base, Language, TaxiService } from "..";

const logger = createLogger({ name: "CustomerBalanceRule" });

class CustomerBalanceRule extends Base {
	static fromResponse(
		data: Record<string, unknown>,
		lang: Language = "uk",
	): CustomerBalanceRule.Model {
		const taxiServices = (data?.taxiServices as TaxiService.Model[]) ?? [];
		const taxiServiceIds = uniq(
			taxiServices
				.map((item) => item?.id)
				.filter((id): id is number => typeof id === "number"),
		);

		const company = (data?.company as CustomerBalanceRule.Model["company"]) ?? null;

		return {
			id: (data?.id as number) ?? 0,
			active: (data?.active as boolean) ?? true,
			name:
				(data?.name as Record<string, string>)?.[lang] ??
				(data?.name as Record<string, string>)?.ru ??
				"",
			company,
			companyId: company?.id ?? 0,
			taxiServices,
			taxiServiceIds,
			warningEnabled: (data?.warningEnabled as boolean) ?? true,
			warningThreshold: Number(data?.warningThreshold ?? 0),
			blockingEnabled: (data?.blockingEnabled as boolean) ?? true,
			blockingThreshold: Number(data?.blockingThreshold ?? 0),
			createdAt: (data?.createdAt as string) ?? "",
			updatedAt: (data?.updatedAt as string) ?? "",
			deletedAt: (data?.deletedAt as string) ?? null,
		};
	}

	static toRequest(
		model: CustomerBalanceRule.Model.New | CustomerBalanceRule.Model.Modified,
	): Record<string, unknown> {
		return {
			name:
				typeof model.name === "string"
					? createObjectLanguageNames(model.name)
					: model.name,
			active: model.active,
			companyId: model.companyId,
			taxiServiceIds: model.taxiServiceIds,
			warningEnabled: model.warningEnabled,
			warningThreshold: model.warningThreshold,
			blockingEnabled: model.blockingEnabled,
			blockingThreshold: model.blockingThreshold,
		};
	}

	public static async getById(id: number, lang?: Language) {
		const data = await this.request((prpc) =>
			(prpc.theirsModel as any).customerBalanceRule.getById(id),
		);
		if (!data) return null;
		return CustomerBalanceRule.fromResponse(data, lang);
	}

	public static async store(
		object: CustomerBalanceRule.Model.New,
		force = false,
	) {
		try {
			const res = await this.request(
				(prpc) =>
					(prpc.theirsModel as any).customerBalanceRule.create(
						CustomerBalanceRule.toRequest(object),
						{ force },
					),
				{ silent: false, error: true },
			);
			logger.info("CustomerBalanceRule store", { object, res, force });
			return res;
		} catch {
			return null;
		}
	}

	public static async update(
		object: CustomerBalanceRule.Model.Modified,
		force = false,
	) {
		try {
			const res = await this.request(
				(prpc) =>
					(prpc.theirsModel as any).customerBalanceRule.update(
						object.id,
						CustomerBalanceRule.toRequest(object),
						{ force },
					),
				{ silent: false, error: true },
			);
			logger.info("CustomerBalanceRule update", { object, res, force });
			return res;
		} catch {
			return null;
		}
	}

	public static async destroy(id: number[] | number) {
		if (Array.isArray(id))
			await Promise.all(id.map((idItem) => this.destroyOne(idItem)));
		else await this.destroyOne(id);
	}

	public static async subscribe(
		options: CustomerBalanceRule.SubscribeOptions,
		onUpdate: SubscriptionBase.OnUpdate<CustomerBalanceRule.Model>,
	): Promise<SubscriptionBase<CustomerBalanceRule.SubscribeOptions> | null> {
		const currentOptionsRef: {
			current: CustomerBalanceRule.SubscribeOptions;
		} = {
			current: options,
		};

		const modelEventConstructor = new ModelEventConstructor({
			onUpdate: (state) => {
				const opts = currentOptionsRef.current;
				const items = compact(state?.models || []);
				const models = compact(
					items.map((item) =>
						CustomerBalanceRule.fromResponse(
							item as Record<string, unknown>,
							opts.lang,
						),
					),
				);

				onUpdate({
					...state,
					models,
				});
			},
		});

		const subscription = await SubscriptionPool.add(
			(prpc) =>
				(prpc.theirsModel as any).customerBalanceRule.subscribe({
					params: CustomerBalanceRule.optionsToRequest(options),
					ping: () => true,
					onEvent: (event: unknown) => {
						modelEventConstructor.onEvent(
							event as Parameters<
								ModelEventConstructor["onEvent"]
							>[0],
						);
					},
					onError: (error: unknown) => {
						logger.error(error);
					},
				}),
			{ name: "CustomerBalanceRule.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (opts: CustomerBalanceRule.SubscribeOptions) => {
				currentOptionsRef.current = opts;
				return subscription.update(
					CustomerBalanceRule.optionsToRequest(opts),
				);
			},
		} as SubscriptionBase<CustomerBalanceRule.SubscribeOptions>;
	}

	private static async destroyOne(id: number) {
		this.request((prpc) =>
			(prpc.theirsModel as any).customerBalanceRule.delete(id),
		);
	}

	private static optionsToRequest(options: CustomerBalanceRule.SubscribeOptions) {
		return {
			query: options.query,
			offset: options.offset,
			limit: options.limit,
			lang: options.lang,
			active: options.active,
			companyIds: options.companyIds,
			taxiServiceIds: options.taxiServiceIds,
			order: options.order,
		};
	}
}

declare namespace CustomerBalanceRule {
	export interface Model {
		id: number;
		active: boolean;
		name: string;
		companyId: number;
		company: {
			id: number;
			name?: Record<string, string>;
		} | null;
		taxiServices: TaxiService.Model[];
		taxiServiceIds: number[];
		warningEnabled: boolean;
		warningThreshold: number;
		blockingEnabled: boolean;
		blockingThreshold: number;
		createdAt: string;
		updatedAt: string;
		deletedAt: string | null;
	}

	interface SubscribeOptions
		extends Omit<
			ServiceSubscribeOptionsBase<CustomerBalanceRule.Model>,
			"order"
		> {
		lang?: Language;
		active?: boolean;
		companyIds?: number[];
		taxiServiceIds?: number[];
		order?: Partial<Record<keyof Model, SortingOrder>>;
	}

	namespace Model {
		interface New {
			name: string;
			active: boolean;
			companyId: number;
			taxiServiceIds: number[];
			warningEnabled: boolean;
			warningThreshold: number;
			blockingEnabled: boolean;
			blockingThreshold: number;
		}
		type Modified = Partial<New> & { id: number };
	}
}

export default CustomerBalanceRule;
